import { CommonModule } from '@angular/common';
import { Component, inject, OnInit } from '@angular/core';
import { LimitedCurrencyAmount } from '@lx/currency/shared/model/currency.model';
import { Amount } from '@lx/shared/data-access/types';
import { getConfiguration } from '@lx/shared/tokens';
import { ButtonComponent } from '@lx/shared/ui/button';
import { PricePipe } from '@lx/shared/ui/pipes';
import { BreakpointObserverService } from '@lx/shared/utils-dom';
import { AccordionModule } from 'primeng/accordion';
import { BehaviorSubject, Observable } from 'rxjs';
import { filter, map } from 'rxjs/operators';
import { PackageDetailsOrchestratorService } from '../../services';
import { QuoteState, QuoteStore, QuoteToolLiteStore } from '../../store';
import { RouteIds } from '../../types';
import { CurrencySelectorComponent, SidePanelComponent } from '@lx/quote-tool/lite/shared/ui/components';
import { QuoteBreakdownComponent } from '../quote-breakdown/quote-breakdown.component';
import { GoogleAnalyticsEventEnum } from '../../google-analytics';
import { NavigationToStepDirective } from '../../directives';
import { Flows, Flow } from '@lx/quote-tool/lite/shared/types';

@Component({
  standalone: true,
  selector: 'lx-total-price',
  templateUrl: './total-price.component.html',
  styleUrls: ['./total-price.component.scss'],
  imports: [
    CommonModule,
    ButtonComponent,
    PricePipe,
    CurrencySelectorComponent,
    AccordionModule,
    QuoteBreakdownComponent,
    SidePanelComponent,
    NavigationToStepDirective,
  ],
})
export class TotalPriceComponent implements OnInit {
  currency$: Observable<string> | undefined;
  grandTotal$: Observable<LimitedCurrencyAmount[]> | undefined;
  activeFlow$: Observable<Flow> | undefined;
  enabledBookNow = false;
  isCurrencyDialogVisible = false;
  quoteBreakdownVisible$ = new BehaviorSubject<boolean>(false);
  handheld$: Observable<boolean> | undefined;
  packageDetails$: Observable<Partial<QuoteState>> | undefined;
  decimalPlaces = 0;
  depositAmounts$: Observable<Amount[] | undefined>;

  // Currency selector observables
  protected selectedCurrency$: Observable<string> | undefined;
  protected singleCurrencyMode$: Observable<boolean> | undefined;
  protected availableCurrencies$: Observable<string[]> | undefined;

  quoteEnrolEventName = GoogleAnalyticsEventEnum.QuoteEnrol;

  nextStep$: Observable<Observable<RouteIds>> | undefined;

  protected readonly Flows = Flows;

  private lastFocusedElement: HTMLElement | undefined;

  private readonly quoteStore = inject(QuoteStore);
  private readonly qtLiteStore = inject(QuoteToolLiteStore);
  private readonly configuration = getConfiguration();
  private readonly packageDetailsOrchestratorService = inject(PackageDetailsOrchestratorService);
  private readonly breakpointObserverService = inject(BreakpointObserverService);

  ngOnInit(): void {
    this.enabledBookNow = <boolean>this.configuration.QUOTE_TOOL_ENROLMENT;
    this.grandTotal$ = this.quoteStore.grandTotal$;
    this.currency$ = this.qtLiteStore.selectedCurrency$;
    this.activeFlow$ = this.qtLiteStore.activeFlow$;
    this.quoteBreakdownVisible$ = this.packageDetailsOrchestratorService.quoteBreakdownVisible$;
    this.handheld$ = this.breakpointObserverService.handheld$;
    this.packageDetails$ = this.quoteStore.packageDetails$;
    this.depositAmounts$ = this.quoteStore.payableData$.pipe(
      filter(Boolean),
      map(({ amounts }) => amounts),
    );

    this.nextStep$ = this.qtLiteStore.currentStepNextRouteId$;

    // Assign currency observables for the currency selector component
    this.selectedCurrency$ = this.qtLiteStore.selectedCurrency$;
    this.singleCurrencyMode$ = this.quoteStore.singleCurrencyMode$;
    this.availableCurrencies$ = this.buildAvailableCurrencies();
  }

  toggleBreakdown() {
    // Store the currently focused element before opening the modal
    this.lastFocusedElement = document.activeElement as HTMLElement;
    this.packageDetailsOrchestratorService.toggleQuoteBreakdownVisibility();
  }

  hideBreakdown() {
    this.packageDetailsOrchestratorService.setQuoteBreakdownVisibility(false);
    // Restore focus to the element that triggered the modal
    this.restoreFocus();
  }

  onCurrencySelectorDialogOpen(isDialogVisible: boolean) {
    this.isCurrencyDialogVisible = isDialogVisible;
  }

  /**
   * Handles currency selection from the currency selector component
   * @param currency - The selected currency code
   * @protected
   */
  protected onCurrencySelected(currency: string): void {
    this.qtLiteStore.updateState({
      selectedCurrency: currency,
    });
  }

  protected handleBreakdownKeydown(event: KeyboardEvent) {
    if (event.key === ' ' || event.key === 'Enter') {
      event.preventDefault();
      this.toggleBreakdown();
    }
  }

  /**
   * Builds an observable of available currencies based on the grand total and single currency mode.
   * @private
   */
  private buildAvailableCurrencies() {
    return this.quoteStore.grandTotalAndSingleCurrencyMode$.pipe(
      map(({ grandTotal, singleCurrencyMode }) => {
        if (!singleCurrencyMode && grandTotal) {
          return grandTotal.reduce((acc, amount) => {
            if (amount.visible && amount.value) {
              acc.push(amount.currency);
            }
            return acc;
          }, [] as string[]);
        }
        return [];
      }),
    );
  }

  private restoreFocus(): void {
    // Use setTimeout to ensure the modal is fully closed before restoring focus
    setTimeout(() => {
      if (this.lastFocusedElement && document.contains(this.lastFocusedElement)) {
        this.lastFocusedElement.focus();
      }
    }, 100);
  }
}
